import * as MD from "reports/measures/MeasureDef";
import * as MP from "reports/measures/MeasureProperties";

// DEPRECATED: Keep enum for backward compatibility during transition
// New code should use severityLevel instead
export enum Severity {
    LOW = 1,
    MILD = 2,
    MODERATE = 3,
    SEVERE = 4,
    VERY_SEVERE = 5,
    EXTREME = 6
}

export type SeverityColors = {
    bg: string;
    border: string;
    text: string;
};

export const severityColors: Record<"severe" | "warn" | "low", SeverityColors> = {
    severe: {
        bg: "bg-error-50",
        border: "border-error-200",
        text: "text-error-700"
    },
    warn: {
        bg: "bg-warning-50",
        border: "border-warning-200",
        text: "text-warning-700"
    },
    low: {
        bg: "bg-success-50",
        border: "border-success-200",
        text: "text-success-700"
    }
};

export type SeverityDetails = {
    label: string;
    severityLevel: number;
    description?: string;
    colors?: SeverityColors;
    functional: boolean;
    severity?: Severity;         // DEPRECATED: Keep for backward compatibility
};

export type SeverityResolver = (measurement: number | MD.Measures) => SeverityDetails;
export type SeverityLevelToColorMapper = (severityLevel: number) => 'low' | 'warn' | 'severe';
export type SeverityLevelToDisplayMapper = (severityLevel: number) => string;

// ---=== Helper Functions ===--- //

/**
 * Gets the color category for a given measure and severity level
 * Uses the generated severityLevelColorMap
 */
export function getSeverityColor(measureType: MD.MeasureType, severityLevel: number): 'low' | 'warn' | 'severe' {
    const mapper = severityLevelColorMap[measureType];
    return mapper ? mapper(severityLevel) : 'warn';
}

/**
 * Gets the display label for a given measure and severity level
 * Uses the generated severityLevelDisplayMap
 */
export function getSeverityLabel(measureType: MD.MeasureType, severityLevel: number): string {
    const mapper = severityLevelDisplayMap[measureType];
    return mapper ? mapper(severityLevel) : 'Unknown';
}

// ==== START GENERATED CODE by severity-resolver-confluence-importer.cjs ====

// ---=== Resolvers ===--- //
//  Do not edit manually - changes will be overwritten by the importer

// Activity (maps to Activity in MeasurePropertiesMap)
export const activityResolver: SeverityResolver = (measurement: number): SeverityDetails => {
    return measurement >= 8
        ? { severityLevel: 1, label: "Very High", description: "Indicates excellent functional capacity with the ability to perform strenuous activities.", colors: severityColors.low, functional: true }
        : measurement >= 6
            ? { severityLevel: 2, label: "High", description: "Indicates good functional capacity with minimal limitations and likely to manage daily activities and most tasks efficiently.", colors: severityColors.low, functional: true }
            : measurement >= 4
                ? { severityLevel: 3, label: "Moderate", description: "Indicates moderate functional capacity with the ability to perform most daily activities. May experience fatigue or difficulty with more strenuous tasks.", colors: severityColors.warn, functional: false }
                : measurement >= 2
                    ? { severityLevel: 4, label: "Low", description: "Indicates some functional limitations. Daily activities may be challenging and require additional effort.", colors: severityColors.warn, functional: false }
                    : { severityLevel: 5, label: "Very Low", description: "Indicates significant functional impairment with considerable limitations in daily activities.", colors: severityColors.severe, functional: false };
};

// BriefPCS (maps to BriefPCS in MeasurePropertiesMap, lowerIsBetter: true)
export const briefPcsResolver: SeverityResolver = (measurement: number): SeverityDetails => {
    return measurement >= 12
        ? { severityLevel: 3, label: "High", description: "Indicates significant catastrophic thinking about pain with severe functional limitations.", colors: severityColors.severe, functional: false }
        : measurement >= 6
            ? { severityLevel: 2, label: "Moderate", description: "Indicates some degree of catastrophic thinking about pain but are still able to function relatively well.", colors: severityColors.warn, functional: false }
            : { severityLevel: 1, label: "Low", description: "Indicates ability to perform daily activities with minimal interruption from negative thinking.", colors: severityColors.low, functional: true };
};

// Confidence (maps to Confidence in MeasurePropertiesMap)
export const confidenceResolver: SeverityResolver = (measurement: number): SeverityDetails => {
    return measurement >= 9
        ? { severityLevel: 1, label: "Very High", description: "Indicates a positive mindset when approaching tasks, with full belief in their abilities.", colors: severityColors.low, functional: true }
        : measurement >= 7
            ? { severityLevel: 2, label: "High", description: "Indicates general positivity about their ability to engage in most tasks and activities with minimal restrictions.", colors: severityColors.low, functional: true }
            : measurement >= 5
                ? { severityLevel: 3, label: "Moderate", description: "Indicates willingness to attempt tasks with caution with some functional limitation.", colors: severityColors.warn, functional: false }
                : measurement >= 3
                    ? { severityLevel: 4, label: "Low", description: "Indicates willingness to attempt some activities but remains hesitant and cautious.", colors: severityColors.warn, functional: false }
                    : { severityLevel: 5, label: "Very Low", description: "Indicates possible decrease in participation in activities of daily living and daily life roles.", colors: severityColors.severe, functional: false };
};

// DASH (maps to DASH in MeasurePropertiesMap, lowerIsBetter: true)
export const dashResolver: SeverityResolver = (measurement: number): SeverityDetails => {
    return measurement >= 81
        ? { severityLevel: 5, label: "Extreme Disability", description: "Indicates minimal to no functional capacity and complete dependence on others for most activities.", colors: severityColors.severe, functional: false }
        : measurement >= 61
            ? { severityLevel: 4, label: "Severe Disability", description: "Indicates low functional capacity with major restrictions in daily activities.", colors: severityColors.severe, functional: false }
            : measurement >= 41
                ? { severityLevel: 3, label: "Moderate Disability", description: "Indicates reduced functional capacity with significant challenges in daily activities.", colors: severityColors.warn, functional: false }
                : measurement >= 21
                    ? { severityLevel: 2, label: "Mild Disability", description: "Indicates ability to perform most daily activities but may need occasional assistance.", colors: severityColors.warn, functional: false }
                    : { severityLevel: 1, label: "Minimal Disability", description: "Indicates high functional capacity and ability to perform most daily activities with minimal difficulty.", colors: severityColors.low, functional: true };
};

// FABQ Physical Activity (maps to FABQActivity in MeasurePropertiesMap, lowerIsBetter: true)
export const fabqActivityResolver: SeverityResolver = (measurement: number): SeverityDetails => {
    return measurement >= 17
        ? { severityLevel: 3, label: "High", description: "Indicates significant avoidance of physical activities and likely to experience poor physical function.", colors: severityColors.severe, functional: false }
        : measurement >= 9
            ? { severityLevel: 2, label: "Moderate", description: "Indicates some avoidance of physical activities and likely to have moderate impact on physical function.", colors: severityColors.warn, functional: false }
            : { severityLevel: 1, label: "Low", description: "Indicates active engagement in physical activities and likely to experience better physical function.", colors: severityColors.low, functional: true };
};

// FABQ Total (maps to FABQTotal in MeasurePropertiesMap, lowerIsBetter: true)
export const fabqTotalResolver: SeverityResolver = (measurement: number): SeverityDetails => {
    return measurement >= 65
        ? { severityLevel: 3, label: "High", description: "Indicates significant impact on function and likely to avoid many activities, leading to greater disability.", colors: severityColors.severe, functional: false }
        : measurement >= 49
            ? { severityLevel: 2, label: "Moderate", description: "Indicates potential impact on function and may avoid some activities due to fear of pain.", colors: severityColors.warn, functional: false }
            : { severityLevel: 1, label: "Low", description: "Indicates minimal impact on function with better physical activity and function.", colors: severityColors.low, functional: true };
};

// FABQ Work (maps to FABQWork in MeasurePropertiesMap, lowerIsBetter: true)
export const fabqWorkResolver: SeverityResolver = (measurement: number): SeverityDetails => {
    return measurement >= 25
        ? { severityLevel: 3, label: "High", description: "Indicates significant avoidance of physical activities and functional tasks, leading to considerable reductions in physical function and activity levels.", colors: severityColors.severe, functional: false }
        : measurement >= 15
            ? { severityLevel: 2, label: "Moderate", description: "Indicates some functional limitations, but are not entirely incapacitated.", colors: severityColors.warn, functional: false }
            : { severityLevel: 1, label: "Low", description: "Indicates higher likelihood to engage in physical activities and work tasks without excessive fear of pain exacerbation.", colors: severityColors.low, functional: true };
};

// GAD-7 (maps to GAD7 in MeasurePropertiesMap, lowerIsBetter: true)
export const gad7Resolver: SeverityResolver = (measurement: number): SeverityDetails => {
    return measurement >= 15
        ? { severityLevel: 4, label: "Severe Anxiety", description: "Indicates severe symptoms, often significantly interfering with daily activities and overall well-being.", colors: severityColors.severe, functional: false }
        : measurement >= 10
            ? { severityLevel: 3, label: "Moderate Anxiety", description: "Indicates symptoms of anxiety are more pronounced and may begin to impact daily functioning and quality of life.", colors: severityColors.warn, functional: false }
            : measurement >= 5
                ? { severityLevel: 2, label: "Mild Anxiety", description: "Indicates some manageable symptoms of anxiety, but may not interfere significantly with daily activities.", colors: severityColors.warn, functional: false }
                : { severityLevel: 1, label: "No Anxiety", description: "Indicates little to no symptoms of anxiety and likely to perform effectively in their life roles.", colors: severityColors.low, functional: true };
};

// Karuna Rating (maps to KarunaRating in MeasurePropertiesMap)
export const karunaRatingResolver: SeverityResolver = (measurement: number): SeverityDetails => {
    return measurement >= 4000
        ? { severityLevel: 1, label: "Minimal/No Limitations", description: "Indicates high levels of physical function. Likely able to perform most daily tasks independently.", colors: severityColors.low, functional: true }
        : measurement >= 2500
            ? { severityLevel: 2, label: "Mild Limitations", description: "Indicates moderate levels of physical function. Likely independent in most daily tasks with some adjustments needed.", colors: severityColors.warn, functional: false }
            : measurement >= 1000
                ? { severityLevel: 3, label: "Moderate Limitations", description: "Indicates low levels of physical function. May be able to perform some basic tasks with difficulty and limited endurance.", colors: severityColors.warn, functional: false }
                : { severityLevel: 4, label: "Severe Limitations", description: "Indicates severe limitations in function. Likely to experience significant mobility limitations and may struggle with basic daily tasks.", colors: severityColors.severe, functional: false };
};

// LEFS-10 (maps to LEFS10 in MeasurePropertiesMap)
export const lefs10Resolver: SeverityResolver = (measurement: number): SeverityDetails => {
    return measurement >= 31
        ? { severityLevel: 1, label: "Minimal/No Limitations", description: "Indicates full or near-full functional independence of the lower extremities.", colors: severityColors.low, functional: true }
        : measurement >= 21
            ? { severityLevel: 2, label: "Mild Limitations", description: "Indicates general independence in walking, transfers, and self-care, with occasional discomfort during high-demand or long-duration activities.", colors: severityColors.warn, functional: false }
            : measurement >= 11
                ? { severityLevel: 3, label: "Moderate Limitations", description: "Indicates partial independence with lower-extremity use but difficulty with endurance, uneven terrain, and stairs.", colors: severityColors.warn, functional: false }
                : { severityLevel: 4, label: "Severe Limitations", description: "Indicates significant restriction in mobility and daily activities. Likely requires assistive support for standing, walking, or transfers.", colors: severityColors.severe, functional: false };
};

// Lower Back Flexion (maps to LowerFlexion in MeasurePropertiesMap)
export const lowerROMResolver: SeverityResolver = (measurement: number): SeverityDetails => {
    return measurement >= 86
        ? { severityLevel: 1, label: "Full Function/Normal", description: "Indicates an ability to perform a wide range of activities without limitations, including bending and lifting.", colors: severityColors.low, functional: true }
        : measurement >= 76
            ? { severityLevel: 2, label: "Functional/Near Normal", description: "Indicates an ability to perform various activities without significant limitation, including bending and lifting.", colors: severityColors.low, functional: true }
            : measurement >= 61
                ? { severityLevel: 3, label: "Minimally Limited", description: "Indicates some difficulty with tasks that involve bending forward or lifting.", colors: severityColors.warn, functional: false }
                : measurement >= 16
                    ? { severityLevel: 4, label: "Moderately Limited", description: "Indicates greater challenges in performing tasks that require forward bending or lifting.", colors: severityColors.warn, functional: false }
                    : { severityLevel: 5, label: "Severely Limited", description: "Indicates significant impairment in tasks involving bending, lifting, and prolonged sitting.", colors: severityColors.severe, functional: false };
};

// Medication Reduction (New) (maps to Medication in MeasurePropertiesMap) [from existing SeverityResolver.tsx]
export const medicationReductionNewResolver: SeverityResolver = (measurement: number): SeverityDetails => {
    return measurement === 1
        ? { severityLevel: 1, label: "Yes", description: "Patient is successfully reducing medication usage", colors: severityColors.low, functional: true }
        : { severityLevel: 2, label: "No", description: "Patient is not reducing medication usage", colors: severityColors.warn, functional: false }
};

// Medication Reduction (Old) (maps to Medication in MeasurePropertiesMap) [from existing SeverityResolver.tsx]
export const medicationReductionOldResolver: SeverityResolver = (measurement: number): SeverityDetails => {
    return measurement === 1
        ? { severityLevel: 1, label: "Yes", description: "Patient is successfully reducing medication usage", colors: severityColors.low, functional: true }
        : { severityLevel: 2, label: "No", description: "Patient is not reducing medication usage", colors: severityColors.warn, functional: false }
};

// Mood (maps to Mood in MeasurePropertiesMap)
export const moodResolver: SeverityResolver = (measurement: number): SeverityDetails => {
    return measurement >= 9
        ? { severityLevel: 1, label: "Very Good", description: "Indicates higher energy and motivation, leading to higher levels of physical activity and function and excels in life roles.", colors: severityColors.low, functional: true }
        : measurement >= 7
            ? { severityLevel: 2, label: "Good", description: "Indicates higher energy and motivation and able to manage challenges and engage in daily functions with resilience.", colors: severityColors.low, functional: true }
            : measurement >= 5
                ? { severityLevel: 3, label: "Fair", description: "Indicates low energy, but still can engage in physical activities at a reduced level.", colors: severityColors.warn, functional: false }
                : measurement >= 3
                    ? { severityLevel: 4, label: "Poor", description: "Indicates fatigue, low energy, and decreased motivation, but may complete essential tasks and responsibilities.", colors: severityColors.warn, functional: false }
                    : { severityLevel: 5, label: "Very Poor", description: "Indicates fatigue, low energy, and decreased motivation, leading to reduced physical activity and participation in daily tasks.", colors: severityColors.severe, functional: false };
};

// NDI (maps to NDI in MeasurePropertiesMap, lowerIsBetter: true)
export const ndiResolver: SeverityResolver = (measurement: number): SeverityDetails => {
    return measurement >= 35
        ? { severityLevel: 5, label: "Complete Disability", description: "Indicates complete disability from neck pain, unable to carry out most daily activities.", colors: severityColors.severe, functional: false }
        : measurement >= 25
            ? { severityLevel: 4, label: "Severe Disability", description: "Indicates severe pain and significant limitations in daily activities, with a substantial impact on their quality of life.", colors: severityColors.severe, functional: false }
            : measurement >= 15
                ? { severityLevel: 3, label: "Moderate Disability", description: "Indicates noticeable pain and moderate limitations in daily activities, affecting their ability to function normally.", colors: severityColors.warn, functional: false }
                : measurement >= 5
                    ? { severityLevel: 2, label: "Mild Disability", description: "Indicates some pain and minor limitations in daily activities, but generally without significant impairment.", colors: severityColors.warn, functional: false }
                    : { severityLevel: 1, label: "No Disability", description: "Indicates no significant impact on daily activities due to neck pain.", colors: severityColors.low, functional: true };
};

// Neck Extension (maps to NeckExtension in MeasurePropertiesMap)
export const neckExtensionROMResolver: SeverityResolver = (measurement: number): SeverityDetails => {
    return measurement >= 58
        ? { severityLevel: 1, label: "Full Function/Normal", description: "Indicates an ability to perform all activities requiring backward head tilt with no limitations.", colors: severityColors.low, functional: true }
        : measurement >= 49
            ? { severityLevel: 2, label: "Functional/Near Normal", description: "Indicates an ability to perform most activities requiring backward head tilt without significant limitation.", colors: severityColors.low, functional: true }
            : measurement >= 37
                ? { severityLevel: 3, label: "Minimally Limited", description: "Indicates some difficulty with tasks requiring backward head tilt, especially with prolonged or repeated tasks.", colors: severityColors.warn, functional: false }
                : measurement >= 25
                    ? { severityLevel: 4, label: "Moderately Limited", description: "Indicates moderate difficulty with tasks requiring backward head tilt.", colors: severityColors.warn, functional: false }
                    : { severityLevel: 5, label: "Severely Limited", description: "Indicates significant difficulty with tasks requiring backward head tilt, including overhead viewing and driving.", colors: severityColors.severe, functional: false };
};

// Neck Flexion (maps to NeckFlexion in MeasurePropertiesMap)
export const neckFlexionROMResolver: SeverityResolver = (measurement: number): SeverityDetails => {
    return measurement >= 48
        ? { severityLevel: 1, label: "Full Function/Normal", description: "Indicates an ability to perform all activities requiring forward neck bending with no limitations.", colors: severityColors.low, functional: true }
        : measurement >= 41
            ? { severityLevel: 2, label: "Functional/Near Normal", description: "Indicates an ability to perform most activities requiring forward neck bending without significant limitation.", colors: severityColors.low, functional: true }
            : measurement >= 31
                ? { severityLevel: 3, label: "Minimally Limited", description: "Indicates some difficulty with tasks requiring forward neck bending, especially with prolonged or repeated tasks.", colors: severityColors.warn, functional: false }
                : measurement >= 21
                    ? { severityLevel: 4, label: "Moderately Limited", description: "Indicates moderate difficulty with tasks requiring forward neck bending.", colors: severityColors.warn, functional: false }
                    : { severityLevel: 5, label: "Severely Limited", description: "Indicates significant difficulty with tasks requiring forward neck bending, including reading, driving, and eating.", colors: severityColors.severe, functional: false };
};

// Neck Rotation (Right/Left) (maps to NeckLeftRotation, NeckRightRotation in MeasurePropertiesMap)
export const neckRotationROMResolver: SeverityResolver = (measurement: number): SeverityDetails => {
    return measurement >= 77
        ? { severityLevel: 1, label: "Full Function/Normal", description: "Indicates an ability to perform all activities requiring head turning with no limitations.", colors: severityColors.low, functional: true }
        : measurement >= 65
            ? { severityLevel: 2, label: "Functional/Near Normal", description: "Indicates an ability to perform most activities requiring head turning without significant limitation.", colors: severityColors.low, functional: true }
            : measurement >= 49
                ? { severityLevel: 3, label: "Minimally Limited", description: "Indicates some difficulty with tasks requiring head turning, possibly with stiffness or limited peripheral awareness.", colors: severityColors.warn, functional: false }
                : measurement >= 33
                    ? { severityLevel: 4, label: "Moderately Limited", description: "Indicates moderate difficulty with tasks requiring head turning; may need head support or full body rotation compensation.", colors: severityColors.warn, functional: false }
                    : { severityLevel: 5, label: "Severely Limited", description: "Indicates significant difficulty with tasks requiring head turning, including driving and personal hygiene.", colors: severityColors.severe, functional: false };
};

// Oswestry (maps to Oswestry in MeasurePropertiesMap, lowerIsBetter: true)
export const oswestryResolver: SeverityResolver = (measurement: number): SeverityDetails => {
    return measurement >= 35
        ? { severityLevel: 5, label: "Complete Disability", description: "Indicates inability to carry out most daily activities independently.", colors: severityColors.severe, functional: false }
        : measurement >= 25
            ? { severityLevel: 4, label: "Severe Disability", description: "Indicates significant pain and substantial limitations in their ability to perform daily activities.", colors: severityColors.severe, functional: false }
            : measurement >= 15
                ? { severityLevel: 3, label: "Moderate Disability", description: "Indicates difficulty with heavier or more strenuous activities.", colors: severityColors.warn, functional: false }
                : measurement >= 5
                    ? { severityLevel: 2, label: "Mild Disability", description: "Indicates general ability to perform most activities, but may experience some pain and slight limitations in function.", colors: severityColors.warn, functional: false }
                    : { severityLevel: 1, label: "No Disability", description: "Indicates minimal or no pain and can perform all activities without significant restriction.", colors: severityColors.low, functional: true };
};

// Pain (maps to Pain in MeasurePropertiesMap, lowerIsBetter: true)
export const painResolver: SeverityResolver = (measurement: number): SeverityDetails => {
    return measurement >= 7
        ? { severityLevel: 4, label: "Severe", description: "Indicates significant interference with daily activities or functions.", colors: severityColors.severe, functional: false }
        : measurement >= 4
            ? { severityLevel: 3, label: "Moderate", description: "Indicates difficulty with heavier or more strenuous activities and may need some assistance for daily tasks.", colors: severityColors.warn, functional: false }
            : measurement >= 1
                ? { severityLevel: 2, label: "Mild", description: "Indicates no significant interference with daily activities or functions.", colors: severityColors.warn, functional: false }
                : { severityLevel: 1, label: "None", description: "Indicates no impact on function.", colors: severityColors.low, functional: true };
};

// PCS (maps to PCS in MeasurePropertiesMap, lowerIsBetter: true)
export const pcsResolver: SeverityResolver = (measurement: number): SeverityDetails => {
    return measurement >= 30
        ? { severityLevel: 3, label: "High", description: "Indicates significant catastrophic thinking about pain with severe functional limitations.", colors: severityColors.severe, functional: false }
        : measurement >= 15
            ? { severityLevel: 2, label: "Moderate", description: "Indicates some degree of catastrophic thinking about pain but are still able to function relatively well.", colors: severityColors.warn, functional: false }
            : { severityLevel: 1, label: "Low", description: "Indicates ability to perform daily activities with minimal interruption from negative thinking.", colors: severityColors.low, functional: true };
};

// PCSS (maps to PCSS in MeasurePropertiesMap, lowerIsBetter: true)
export const pcssResolver: SeverityResolver = (measurement: number): SeverityDetails => {
    return measurement >= 76
        ? { severityLevel: 6, label: "Extreme Symptoms", description: "Indicates symptoms are debilitating, severely limiting the ability to carry out daily activities. May require substantial support.", colors: severityColors.severe, functional: false }
        : measurement >= 61
            ? { severityLevel: 5, label: "Very Severe Symptoms", description: "Indicates symptoms are very severe, causing major disruptions to daily life and functioning.", colors: severityColors.severe, functional: false }
            : measurement >= 46
                ? { severityLevel: 4, label: "Severe Symptoms", description: "Indicates symptoms are significantly interfering with daily activities and may have difficulty with life functions.", colors: severityColors.severe, functional: false }
                : measurement >= 31
                    ? { severityLevel: 3, label: "Moderate Symptoms", description: "Indicates symptoms are significantly interfering with daily activities and life functions.", colors: severityColors.warn, functional: false }
                    : measurement >= 16
                        ? { severityLevel: 2, label: "Mild Symptoms", description: "Indicates mild symptoms with possible disruption to daily activities, but can still function relatively well.", colors: severityColors.warn, functional: false }
                        : { severityLevel: 1, label: "Minimal/No Symptoms", description: "Indicates minimal or mild symptoms, generally not interfering with daily activities.", colors: severityColors.low, functional: true };
};

// PHQ-9 (maps to PHQ9 in MeasurePropertiesMap, lowerIsBetter: true)
export const phq9Resolver: SeverityResolver = (measurement: number): SeverityDetails => {
    return measurement >= 20
        ? { severityLevel: 5, label: "Severe Depression", description: "Indicates debilitating symptoms, making it very difficult to function in daily life.", colors: severityColors.severe, functional: false }
        : measurement >= 15
            ? { severityLevel: 4, label: "Moderately Severe Depression", description: "Indicates daily functioning is significantly impacted and may struggle with daily life functions.", colors: severityColors.warn, functional: false }
            : measurement >= 10
                ? { severityLevel: 3, label: "Moderate Depression", description: "Indicates decreased concentration, energy, and interest in daily activities making them difficult to complete.", colors: severityColors.warn, functional: false }
                : measurement >= 5
                    ? { severityLevel: 2, label: "Mild Depression", description: "Indicates minor difficulties in daily activities, but can generally manage their responsibilities.", colors: severityColors.warn, functional: false }
                    : { severityLevel: 1, label: "Minimal Depression", description: "Indicates minimal impact on daily functioning, including work, social activities, and personal care.", colors: severityColors.low, functional: true };
};

// PSEQ-2 (maps to PSEQ2 in MeasurePropertiesMap)
export const pseq2Resolver: SeverityResolver = (measurement: number): SeverityDetails => {
    return measurement >= 8
        ? { severityLevel: 1, label: "High", description: "Indicates high confidence in their ability to manage pain and perform various activities. leading to better physical function.", colors: severityColors.low, functional: true }
        : measurement >= 6
            ? { severityLevel: 2, label: "Moderate", description: "Indicates some confidence in their ability to perform activities, but with some limitations requiring support.", colors: severityColors.warn, functional: false }
            : { severityLevel: 3, label: "Low", description: "Indicates low confidence in their ability to perform activities despite pain, leading to reduced physical activity and greater disability.", colors: severityColors.severe, functional: false };
};

// PSEQ (maps to PSEQ in MeasurePropertiesMap)
export const pseqResolver: SeverityResolver = (measurement: number): SeverityDetails => {
    return measurement >= 41
        ? { severityLevel: 1, label: "High", description: "Indicates high confidence in their ability to manage pain and perform various activities. leading to better physical function.", colors: severityColors.low, functional: true }
        : measurement >= 21
            ? { severityLevel: 2, label: "Moderate", description: "Indicates some confidence in their ability to perform activities, but with some limitations requiring support.", colors: severityColors.warn, functional: false }
            : { severityLevel: 3, label: "Low", description: "Indicates low confidence in their ability to perform activities despite pain, leading to reduced physical activity and greater disability.", colors: severityColors.severe, functional: false };
};

// QuickDASH (maps to QuickDASH in MeasurePropertiesMap, lowerIsBetter: true)
export const quickDashResolver: SeverityResolver = (measurement: number): SeverityDetails => {
    return measurement >= 81
        ? { severityLevel: 5, label: "Extreme Disability", description: "Indicates minimal to no functional capacity and complete dependence on others for most activities.", colors: severityColors.severe, functional: false }
        : measurement >= 61
            ? { severityLevel: 4, label: "Severe Disability", description: "Indicates low functional capacity with major restrictions in daily activities.", colors: severityColors.severe, functional: false }
            : measurement >= 41
                ? { severityLevel: 3, label: "Moderate Disability", description: "Indicates reduced functional capacity with significant challenges in daily activities.", colors: severityColors.warn, functional: false }
                : measurement >= 21
                    ? { severityLevel: 2, label: "Mild Disability", description: "Indicates ability to perform most daily activities but may need occasional assistance.", colors: severityColors.warn, functional: false }
                    : { severityLevel: 1, label: "Minimal Disability", description: "Indicates high functional capacity and ability to perform most daily activities with minimal difficulty.", colors: severityColors.low, functional: true };
};

// Return To Work (Old) (maps to Work in MeasurePropertiesMap) [from existing SeverityResolver.tsx]
export const returnToWorkOldResolver: SeverityResolver = (measurement: number): SeverityDetails => {
    return measurement >= 3
        ? { severityLevel: 1, label: "Yes", description: "Patient has successfully returned to work.",colors: severityColors.low, functional: true }
        : measurement > 0
            ? { severityLevel: 2, label: "No", description: "Patient has not yet returned to work.", colors: severityColors.warn, functional: false }
            : { severityLevel: 3, label: "N/A", description: "Patient's return to work status is unknown.", colors: severityColors.warn, functional: false };
};

// RPQ-13 (maps to RPQ13 in MeasurePropertiesMap, lowerIsBetter: true)
export const rpq13Resolver: SeverityResolver = (measurement: number): SeverityDetails => {
    return measurement >= 40
        ? { severityLevel: 4, label: "Severe Symptoms", description: "Indicates significant pervasive symptoms affecting cognitive, emotional and sensory domains. Fatigue, confusion, or mood volatility limit daily functioning.", colors: severityColors.severe, functional: false }
        : measurement >= 25
            ? { severityLevel: 3, label: "Moderate Symptoms", description: "Indicates persistent cognitive and emotional interference; reduced stress tolerance impacting daily activities.", colors: severityColors.warn, functional: false }
            : measurement >= 10
                ? { severityLevel: 2, label: "Mild Symptoms", description: "Indicates slight changes in attention, mood, or sleep; reduced multitasking efficiency, mild irritability, and sensory sensitivity.", colors: severityColors.warn, functional: false }
                : { severityLevel: 1, label: "Minimal/No Symptoms", description: "Indicates full or near-baseline cognitive and emotional function. Able to perform daily activities without restriction.", colors: severityColors.low, functional: true };
};

// RPQ-3 (maps to RPQ3 in MeasurePropertiesMap, lowerIsBetter: true)
export const rpq3Resolver: SeverityResolver = (measurement: number): SeverityDetails => {
    return measurement >= 9
        ? { severityLevel: 4, label: "Severe Symptoms", description: "Indicates substantial and persistent headache, dizziness, and nausea that significantly restricts standing, balance, and movement.", colors: severityColors.severe, functional: false }
        : measurement >= 6
            ? { severityLevel: 3, label: "Moderate Symptoms", description: "Indicates consistent headaches, dizziness, or nausea that interfere with concentration and activity tolerance.", colors: severityColors.warn, functional: false }
            : measurement >= 3
                ? { severityLevel: 2, label: "Mild Symptoms", description: "Indicates intermittent or low-grade physical symptoms that may arise with fatigue, stress, or sensory stimulation.", colors: severityColors.warn, functional: false }
                : { severityLevel: 1, label: "Minimal/No Symptoms", description: "Indicates near-complete physical recovery. Headache, dizziness, and nausea are absent or negligible. No interference with daily life and activities.", colors: severityColors.low, functional: true };
};

// RPQ (maps to RPQ in MeasurePropertiesMap, lowerIsBetter: true)
export const rpqResolver: SeverityResolver = (measurement: number): SeverityDetails => {
    return measurement >= 40
        ? { severityLevel: 4, label: "Severe Symptoms", description: "Indicates pervasive symptoms across domains; poor endurance, sleep disruption, and emotional dysregulation. Performance consistency and endurance are significantly reduced.", colors: severityColors.severe, functional: false }
        : measurement >= 25
            ? { severityLevel: 3, label: "Moderate Symptoms", description: "Indicates noticeable interference in concentration, energy, or mood regulation; reduced tolerance to sensory input impacting daily activities.", colors: severityColors.warn, functional: false }
            : measurement >= 10
                ? { severityLevel: 2, label: "Mild Symptoms", description: "Indicates occasional cognitive or sensory overload, but able to do daily activities at a reduced level; minor fatigue or irritability under stress.", colors: severityColors.warn, functional: false }
                : { severityLevel: 1, label: "Minimal/No Symptoms", description: "Indicates normal or near-baseline function. No significant interference in quality of life or performance.", colors: severityColors.low, functional: true };
};

// Sleep (maps to Sleep in MeasurePropertiesMap)
export const sleepResolver: SeverityResolver = (measurement: number): SeverityDetails => {
    return measurement >= 9
        ? { severityLevel: 1, label: "Excellent", description: "Indicates optimal energy and physical performance, with little to no fatigue.", colors: severityColors.low, functional: true }
        : measurement >= 7
            ? { severityLevel: 2, label: "Good", description: "Indicates good energy levels and physical performance, slightly reduced productivity.", colors: severityColors.low, functional: true }
            : measurement >= 5
                ? { severityLevel: 3, label: "Fair", description: "Indicates mild fatigue, but generally able to perform daily activities.", colors: severityColors.warn, functional: false }
                : measurement >= 3
                    ? { severityLevel: 4, label: "Poor", description: "Indicates noticeable fatigue and reduced stamina, with moderate impact on physical activities.", colors: severityColors.warn, functional: false }
                    : { severityLevel: 5, label: "Very Poor", description: "Indicates extreme fatigue and decreased physical performance.", colors: severityColors.severe, functional: false };
};

// Social Function (maps to SocialFunction in MeasurePropertiesMap)
export const socialFunctionResolver: SeverityResolver = (measurement: number): SeverityDetails => {
    return measurement >= 9
        ? { severityLevel: 1, label: "Thriving Socially", description: "Indicates regular, meaningful connections with friends, family, and community. Feels emotionally supported and social engagement is a resource for resilience and joy.", colors: severityColors.low, functional: true }
        : measurement >= 7
            ? { severityLevel: 2, label: "Well Connected", description: "Indicates consistent contact with a support network and feels generally connected. May avoid social plans due to pain or fatigue. Feels emotionally safe sharing personal challenges with at lease one person.", colors: severityColors.low, functional: true }
            : measurement >= 5
                ? { severityLevel: 3, label: "Moderately Connected", description: "Indicates limited, but present social support. Inconsistent emotional connection. Social withdrawal may occur during a flare-up or high stress, impacting mood and motivation.", colors: severityColors.warn, functional: false }
                : measurement >= 3
                    ? { severityLevel: 4, label: "Limited Support", description: "Indicates clear signs of withdrawal. May cancel plans frequently and avoids interactions due to pain or feels emotionally disconnected. Support network may be minimal or strained.", colors: severityColors.warn, functional: false }
                    : { severityLevel: 5, label: "Social Isolation", description: "Indicates significant disconnection and avoids most social interactions. Support network is minimal or strained. Pain is closely tied to lack of social loss. Lacks social engagement motivation or capacity.", colors: severityColors.severe, functional: false };
};

// UEFI (maps to UEFI in MeasurePropertiesMap)
export const uefiResolver: SeverityResolver = (measurement: number): SeverityDetails => {
    return measurement >= 61
        ? { severityLevel: 1, label: "Minimal/No Limitations", description: "Indicates full independence and high function in daily activities and work.", colors: severityColors.low, functional: true }
        : measurement >= 41
            ? { severityLevel: 2, label: "Mild Limitations", description: "Indicates good overall function and most likely independent in daily activities, with minor assistance.", colors: severityColors.warn, functional: false }
            : measurement >= 21
                ? { severityLevel: 3, label: "Moderate Limitations", description: "Indicates some need for assistance or modifications, and notable impact on daily activities.", colors: severityColors.warn, functional: false }
                : { severityLevel: 4, label: "Severe Limitations", description: "Indicates severe limitations in function, significant need for assistance, and high impact on daily living activities.", colors: severityColors.severe, functional: false };
};

// [Left, Right] Shoulder [Flexion, Abduction, Scaption](Upper Extremity) (maps to LeftAbduction, LeftFlexion, LeftScaption, RightAbduction, RightFlexion, RightScaption in MeasurePropertiesMap)
export const upperROMResolver: SeverityResolver = (measurement: number): SeverityDetails => {
    return measurement >= 165
        ? { severityLevel: 1, label: "Full Function", description: "Indicates ability to perform various activities without limitation.", colors: severityColors.low, functional: true }
        : measurement >= 145
            ? { severityLevel: 2, label: "Functional/Near Normal", description: "Indicates ability to perform various activities without significant limitation.", colors: severityColors.low, functional: true }
            : measurement >= 120
                ? { severityLevel: 3, label: "Minimally Limited", description: "Indicates slight difficulty with tasks requiring shoulder movement.", colors: severityColors.warn, functional: false }
                : measurement >= 30
                    ? { severityLevel: 4, label: "Moderately Limited", description: "Indicates increased difficulty with tasks requiring shoulder movement.", colors: severityColors.warn, functional: false }
                    : { severityLevel: 5, label: "Severely Limited", description: "Indicates significant challenges with shoulder tasks, especially lifting, reaching, or overhead activities.", colors: severityColors.severe, functional: false };
};

// WHODAS (maps to WHODAS in MeasurePropertiesMap, lowerIsBetter: true)
export const whodasResolver: SeverityResolver = (measurement: number): SeverityDetails => {
    return measurement >= 4.5
        ? { severityLevel: 5, label: "Extreme Disability", description: "Indicates major limitations in daily activities and overall functioning. Likely dependent on others for most tasks.", colors: severityColors.severe, functional: false }
        : measurement >= 3.5
            ? { severityLevel: 4, label: "Severe Disability", description: "Indicates significant limitations in performing daily activities. Likely require substantial assistance and support.", colors: severityColors.severe, functional: false }
            : measurement >= 2.5
                ? { severityLevel: 3, label: "Moderate Disability", description: "Indicates moderate difficulties in performing daily tasks. These limitations can impact work, social participation, and quality of life.", colors: severityColors.warn, functional: false }
                : measurement >= 1.5
                    ? { severityLevel: 2, label: "Mild Disability", description: "Indicates slight difficulties in performing certain tasks, but these do not significantly affect their overall independence or quality of life.", colors: severityColors.warn, functional: false }
                    : { severityLevel: 1, label: "No Disability", description: "Indicates minimal interference with daily activities and overall functioning.", colors: severityColors.low, functional: true };
};

// Work Status Improved (New) (maps to Work in MeasurePropertiesMap) [from existing SeverityResolver.tsx]
export const workStatusImprovedNewResolver: SeverityResolver = (measurement: number): SeverityDetails => {
    return measurement >= 3
        ? { severityLevel: 1, label: "Yes", description: "Patient has successfully returned to work.",colors: severityColors.low, functional: true }
        : measurement > 0
            ? { severityLevel: 2, label: "No", description: "Patient has not yet returned to work.", colors: severityColors.warn, functional: false }
            : { severityLevel: 3, label: "N/A", description: "Patient's work status improvement is unknown.", colors: severityColors.warn, functional: false };
};

// ---=== Dynamic Mapping Functions ===--- //
//  Do not edit manually - changes will be overwritten by the importer

export const severityLevelColorMap: Partial<Record<MD.MeasureType, SeverityLevelToColorMapper>> = {
    Activity: (level: number) => {
        if (level === 5) return 'severe';
        if (level === 4) return 'warn';
        if (level === 3) return 'warn';
        if (level === 2) return 'low';
        return 'low';
    },
    BriefPCS: (level: number) => {
        if (level === 1) return 'low';
        if (level === 2) return 'warn';
        return 'severe';
    },
    Confidence: (level: number) => {
        if (level === 5) return 'severe';
        if (level === 4) return 'warn';
        if (level === 3) return 'warn';
        if (level === 2) return 'low';
        return 'low';
    },
    DASH: (level: number) => {
        if (level === 1) return 'low';
        if (level === 2) return 'warn';
        if (level === 3) return 'warn';
        if (level === 4) return 'severe';
        return 'severe';
    },
    FABQActivity: (level: number) => {
        if (level === 1) return 'low';
        if (level === 2) return 'warn';
        return 'severe';
    },
    FABQTotal: (level: number) => {
        if (level === 1) return 'low';
        if (level === 2) return 'warn';
        return 'severe';
    },
    FABQWork: (level: number) => {
        if (level === 1) return 'low';
        if (level === 2) return 'warn';
        return 'severe';
    },
    GAD7: (level: number) => {
        if (level === 1) return 'low';
        if (level === 2) return 'warn';
        if (level === 3) return 'warn';
        return 'severe';
    },
    KarunaRating: (level: number) => {
        if (level === 4) return 'severe';
        if (level === 3) return 'warn';
        if (level === 2) return 'warn';
        return 'low';
    },
    LEFS10: (level: number) => {
        if (level === 4) return 'severe';
        if (level === 3) return 'warn';
        if (level === 2) return 'warn';
        return 'low';
    },
    LowerFlexion: (level: number) => {
        if (level === 5) return 'severe';
        if (level === 4) return 'warn';
        if (level === 3) return 'warn';
        if (level === 2) return 'low';
        return 'low';
    },
    Mood: (level: number) => {
        if (level === 5) return 'severe';
        if (level === 4) return 'warn';
        if (level === 3) return 'warn';
        if (level === 2) return 'low';
        return 'low';
    },
    NDI: (level: number) => {
        if (level === 1) return 'low';
        if (level === 2) return 'warn';
        if (level === 3) return 'warn';
        if (level === 4) return 'severe';
        return 'severe';
    },
    NeckExtension: (level: number) => {
        if (level === 5) return 'severe';
        if (level === 4) return 'warn';
        if (level === 3) return 'warn';
        if (level === 2) return 'low';
        return 'low';
    },
    NeckFlexion: (level: number) => {
        if (level === 5) return 'severe';
        if (level === 4) return 'warn';
        if (level === 3) return 'warn';
        if (level === 2) return 'low';
        return 'low';
    },
    NeckLeftRotation: (level: number) => {
        if (level === 5) return 'severe';
        if (level === 4) return 'warn';
        if (level === 3) return 'warn';
        if (level === 2) return 'low';
        return 'low';
    },
    NeckRightRotation: (level: number) => {
        if (level === 5) return 'severe';
        if (level === 4) return 'warn';
        if (level === 3) return 'warn';
        if (level === 2) return 'low';
        return 'low';
    },
    Oswestry: (level: number) => {
        if (level === 1) return 'low';
        if (level === 2) return 'warn';
        if (level === 3) return 'warn';
        if (level === 4) return 'severe';
        return 'severe';
    },
    Pain: (level: number) => {
        if (level === 1) return 'low';
        if (level === 2) return 'warn';
        if (level === 3) return 'warn';
        return 'severe';
    },
    PCS: (level: number) => {
        if (level === 1) return 'low';
        if (level === 2) return 'warn';
        return 'severe';
    },
    PCSS: (level: number) => {
        if (level === 1) return 'low';
        if (level === 2) return 'warn';
        if (level === 3) return 'warn';
        if (level === 4) return 'severe';
        if (level === 5) return 'severe';
        return 'severe';
    },
    PHQ9: (level: number) => {
        if (level === 1) return 'low';
        if (level === 2) return 'warn';
        if (level === 3) return 'warn';
        if (level === 4) return 'warn';
        return 'severe';
    },
    PSEQ2: (level: number) => {
        if (level === 3) return 'severe';
        if (level === 2) return 'warn';
        return 'low';
    },
    PSEQ: (level: number) => {
        if (level === 3) return 'severe';
        if (level === 2) return 'warn';
        return 'low';
    },
    QuickDASH: (level: number) => {
        if (level === 1) return 'low';
        if (level === 2) return 'warn';
        if (level === 3) return 'warn';
        if (level === 4) return 'severe';
        return 'severe';
    },
    RPQ13: (level: number) => {
        if (level === 1) return 'low';
        if (level === 2) return 'warn';
        if (level === 3) return 'warn';
        return 'severe';
    },
    RPQ3: (level: number) => {
        if (level === 1) return 'low';
        if (level === 2) return 'warn';
        if (level === 3) return 'warn';
        return 'severe';
    },
    RPQ: (level: number) => {
        if (level === 1) return 'low';
        if (level === 2) return 'warn';
        if (level === 3) return 'warn';
        return 'severe';
    },
    Sleep: (level: number) => {
        if (level === 5) return 'severe';
        if (level === 4) return 'warn';
        if (level === 3) return 'warn';
        if (level === 2) return 'low';
        return 'low';
    },
    SocialFunction: (level: number) => {
        if (level === 5) return 'severe';
        if (level === 4) return 'warn';
        if (level === 3) return 'warn';
        if (level === 2) return 'low';
        return 'low';
    },
    UEFI: (level: number) => {
        if (level === 4) return 'severe';
        if (level === 3) return 'warn';
        if (level === 2) return 'warn';
        return 'low';
    },
    LeftAbduction: (level: number) => {
        if (level === 5) return 'severe';
        if (level === 4) return 'warn';
        if (level === 3) return 'warn';
        if (level === 2) return 'low';
        return 'low';
    },
    LeftFlexion: (level: number) => {
        if (level === 5) return 'severe';
        if (level === 4) return 'warn';
        if (level === 3) return 'warn';
        if (level === 2) return 'low';
        return 'low';
    },
    LeftScaption: (level: number) => {
        if (level === 5) return 'severe';
        if (level === 4) return 'warn';
        if (level === 3) return 'warn';
        if (level === 2) return 'low';
        return 'low';
    },
    RightAbduction: (level: number) => {
        if (level === 5) return 'severe';
        if (level === 4) return 'warn';
        if (level === 3) return 'warn';
        if (level === 2) return 'low';
        return 'low';
    },
    RightFlexion: (level: number) => {
        if (level === 5) return 'severe';
        if (level === 4) return 'warn';
        if (level === 3) return 'warn';
        if (level === 2) return 'low';
        return 'low';
    },
    RightScaption: (level: number) => {
        if (level === 5) return 'severe';
        if (level === 4) return 'warn';
        if (level === 3) return 'warn';
        if (level === 2) return 'low';
        return 'low';
    },
    WHODAS: (level: number) => {
        if (level === 1) return 'low';
        if (level === 2) return 'warn';
        if (level === 3) return 'warn';
        if (level === 4) return 'severe';
        return 'severe';
    },
};

export const severityLevelDisplayMap: Partial<Record<MD.MeasureType, SeverityLevelToDisplayMapper>> = {
    Activity: (level: number) => {
        if (level === 5) return 'Very Low';
        if (level === 4) return 'Low';
        if (level === 3) return 'Moderate';
        if (level === 2) return 'High';
        return 'Very High';
    },
    BriefPCS: (level: number) => {
        if (level === 1) return 'Low';
        if (level === 2) return 'Moderate';
        return 'High';
    },
    Confidence: (level: number) => {
        if (level === 5) return 'Very Low';
        if (level === 4) return 'Low';
        if (level === 3) return 'Moderate';
        if (level === 2) return 'High';
        return 'Very High';
    },
    DASH: (level: number) => {
        if (level === 1) return 'Minimal Disability';
        if (level === 2) return 'Mild Disability';
        if (level === 3) return 'Moderate Disability';
        if (level === 4) return 'Severe Disability';
        return 'Extreme Disability';
    },
    FABQActivity: (level: number) => {
        if (level === 1) return 'Low';
        if (level === 2) return 'Moderate';
        return 'High';
    },
    FABQTotal: (level: number) => {
        if (level === 1) return 'Low';
        if (level === 2) return 'Moderate';
        return 'High';
    },
    FABQWork: (level: number) => {
        if (level === 1) return 'Low';
        if (level === 2) return 'Moderate';
        return 'High';
    },
    GAD7: (level: number) => {
        if (level === 1) return 'No Anxiety';
        if (level === 2) return 'Mild Anxiety';
        if (level === 3) return 'Moderate Anxiety';
        return 'Severe Anxiety';
    },
    KarunaRating: (level: number) => {
        if (level === 4) return 'Severe Limitations';
        if (level === 3) return 'Moderate Limitations';
        if (level === 2) return 'Mild Limitations';
        return 'Minimal/No Limitations';
    },
    LEFS10: (level: number) => {
        if (level === 4) return 'Severe Limitations';
        if (level === 3) return 'Moderate Limitations';
        if (level === 2) return 'Mild Limitations';
        return 'Minimal/No Limitations';
    },
    LowerFlexion: (level: number) => {
        if (level === 5) return 'Severely Limited';
        if (level === 4) return 'Moderately Limited';
        if (level === 3) return 'Minimally Limited';
        if (level === 2) return 'Functional/Near Normal';
        return 'Full Function/Normal';
    },
    Mood: (level: number) => {
        if (level === 5) return 'Very Poor';
        if (level === 4) return 'Poor';
        if (level === 3) return 'Fair';
        if (level === 2) return 'Good';
        return 'Very Good';
    },
    NDI: (level: number) => {
        if (level === 1) return 'No Disability';
        if (level === 2) return 'Mild Disability';
        if (level === 3) return 'Moderate Disability';
        if (level === 4) return 'Severe Disability';
        return 'Complete Disability';
    },
    NeckExtension: (level: number) => {
        if (level === 5) return 'Severely Limited';
        if (level === 4) return 'Moderately Limited';
        if (level === 3) return 'Minimally Limited';
        if (level === 2) return 'Functional/Near Normal';
        return 'Full Function/Normal';
    },
    NeckFlexion: (level: number) => {
        if (level === 5) return 'Severely Limited';
        if (level === 4) return 'Moderately Limited';
        if (level === 3) return 'Minimally Limited';
        if (level === 2) return 'Functional/Near Normal';
        return 'Full Function/Normal';
    },
    NeckLeftRotation: (level: number) => {
        if (level === 5) return 'Severely Limited';
        if (level === 4) return 'Moderately Limited';
        if (level === 3) return 'Minimally Limited';
        if (level === 2) return 'Functional/Near Normal';
        return 'Full Function/Normal';
    },
    NeckRightRotation: (level: number) => {
        if (level === 5) return 'Severely Limited';
        if (level === 4) return 'Moderately Limited';
        if (level === 3) return 'Minimally Limited';
        if (level === 2) return 'Functional/Near Normal';
        return 'Full Function/Normal';
    },
    Oswestry: (level: number) => {
        if (level === 1) return 'No Disability';
        if (level === 2) return 'Mild Disability';
        if (level === 3) return 'Moderate Disability';
        if (level === 4) return 'Severe Disability';
        return 'Complete Disability';
    },
    Pain: (level: number) => {
        if (level === 1) return 'None';
        if (level === 2) return 'Mild';
        if (level === 3) return 'Moderate';
        return 'Severe';
    },
    PCS: (level: number) => {
        if (level === 1) return 'Low';
        if (level === 2) return 'Moderate';
        return 'High';
    },
    PCSS: (level: number) => {
        if (level === 1) return 'Minimal/No Symptoms';
        if (level === 2) return 'Mild Symptoms';
        if (level === 3) return 'Moderate Symptoms';
        if (level === 4) return 'Severe Symptoms';
        if (level === 5) return 'Very Severe Symptoms';
        return 'Extreme Symptoms';
    },
    PHQ9: (level: number) => {
        if (level === 1) return 'Minimal Depression';
        if (level === 2) return 'Mild Depression';
        if (level === 3) return 'Moderate Depression';
        if (level === 4) return 'Moderately Severe Depression';
        return 'Severe Depression';
    },
    PSEQ2: (level: number) => {
        if (level === 3) return 'Low';
        if (level === 2) return 'Moderate';
        return 'High';
    },
    PSEQ: (level: number) => {
        if (level === 3) return 'Low';
        if (level === 2) return 'Moderate';
        return 'High';
    },
    QuickDASH: (level: number) => {
        if (level === 1) return 'Minimal Disability';
        if (level === 2) return 'Mild Disability';
        if (level === 3) return 'Moderate Disability';
        if (level === 4) return 'Severe Disability';
        return 'Extreme Disability';
    },
    RPQ13: (level: number) => {
        if (level === 1) return 'Minimal/No Symptoms';
        if (level === 2) return 'Mild Symptoms';
        if (level === 3) return 'Moderate Symptoms';
        return 'Severe Symptoms';
    },
    RPQ3: (level: number) => {
        if (level === 1) return 'Minimal/No Symptoms';
        if (level === 2) return 'Mild Symptoms';
        if (level === 3) return 'Moderate Symptoms';
        return 'Severe Symptoms';
    },
    RPQ: (level: number) => {
        if (level === 1) return 'Minimal/No Symptoms';
        if (level === 2) return 'Mild Symptoms';
        if (level === 3) return 'Moderate Symptoms';
        return 'Severe Symptoms';
    },
    Sleep: (level: number) => {
        if (level === 5) return 'Very Poor';
        if (level === 4) return 'Poor';
        if (level === 3) return 'Fair';
        if (level === 2) return 'Good';
        return 'Excellent';
    },
    SocialFunction: (level: number) => {
        if (level === 5) return 'Social Isolation';
        if (level === 4) return 'Limited Support';
        if (level === 3) return 'Moderately Connected';
        if (level === 2) return 'Well Connected';
        return 'Thriving Socially';
    },
    UEFI: (level: number) => {
        if (level === 4) return 'Severe Limitations';
        if (level === 3) return 'Moderate Limitations';
        if (level === 2) return 'Mild Limitations';
        return 'Minimal/No Limitations';
    },
    LeftAbduction: (level: number) => {
        if (level === 5) return 'Severely Limited';
        if (level === 4) return 'Moderately Limited';
        if (level === 3) return 'Minimally Limited';
        if (level === 2) return 'Functional/Near Normal';
        return 'Full Function';
    },
    LeftFlexion: (level: number) => {
        if (level === 5) return 'Severely Limited';
        if (level === 4) return 'Moderately Limited';
        if (level === 3) return 'Minimally Limited';
        if (level === 2) return 'Functional/Near Normal';
        return 'Full Function';
    },
    LeftScaption: (level: number) => {
        if (level === 5) return 'Severely Limited';
        if (level === 4) return 'Moderately Limited';
        if (level === 3) return 'Minimally Limited';
        if (level === 2) return 'Functional/Near Normal';
        return 'Full Function';
    },
    RightAbduction: (level: number) => {
        if (level === 5) return 'Severely Limited';
        if (level === 4) return 'Moderately Limited';
        if (level === 3) return 'Minimally Limited';
        if (level === 2) return 'Functional/Near Normal';
        return 'Full Function';
    },
    RightFlexion: (level: number) => {
        if (level === 5) return 'Severely Limited';
        if (level === 4) return 'Moderately Limited';
        if (level === 3) return 'Minimally Limited';
        if (level === 2) return 'Functional/Near Normal';
        return 'Full Function';
    },
    RightScaption: (level: number) => {
        if (level === 5) return 'Severely Limited';
        if (level === 4) return 'Moderately Limited';
        if (level === 3) return 'Minimally Limited';
        if (level === 2) return 'Functional/Near Normal';
        return 'Full Function';
    },
    WHODAS: (level: number) => {
        if (level === 1) return 'No Disability';
        if (level === 2) return 'Mild Disability';
        if (level === 3) return 'Moderate Disability';
        if (level === 4) return 'Severe Disability';
        return 'Extreme Disability';
    },
};

// ==== END GENERATED CODE by severity-resolver-confluence-importer.cjs ====